import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Target, Plus, Edit2, Trash2, TrendingUp, Calendar, CheckSquare, Folder,
  X, AlertTriangle, ChevronDown, ChevronRight, Scissors, ClipboardList,
  RefreshCw, Users, Building2, Clock, Save, BarChart3, Search, ExternalLink,
  RotateCcw, Filter
} from 'lucide-react';
import { API_URL } from '../config';


// ── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '';
const fmtNum = (n, unite) => {
  if (n == null || n === '') return '';
  const num = parseFloat(n);
  if (isNaN(num)) return n;
  if (unite === 'EUR' || unite === '€')
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(num);
  return `${new Intl.NumberFormat('fr-FR').format(num)} ${unite || ''}`.trim();
};
const pct = (a, b) => b > 0 ? Math.min(100, Math.round((a / b) * 100)) : 0;

const statusOpts = [
  { value: 'en_cours', label: 'En cours', bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-300' },
  { value: 'termine', label: 'Terminé', bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700' },
  { value: 'atteint', label: 'Atteint', bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300' },
  { value: 'en_pause', label: 'En pause', bg: 'bg-yellow-100 dark:bg-yellow-900/40', text: 'text-yellow-700 dark:text-yellow-300' },
  { value: 'en_retard', label: 'En retard', bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700' },
  { value: 'annule', label: 'Annulé', bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300' },
];
const getStatusStyle = (s) => statusOpts.find(o => o.value === s) || statusOpts[0];

// Couleur de la barre de progression
const progressColor = (val) => val >= 75 ? 'bg-green-500' : val >= 50 ? 'bg-blue-500' : val >= 25 ? 'bg-amber-400' : 'bg-red-400';

// Couleurs d'indentation par niveau (inspiré Smartsheet)
const indentColors = ['border-blue-500', 'border-blue-300 dark:border-blue-700', 'border-indigo-200 dark:border-indigo-800', 'border-purple-200 dark:border-purple-800', 'border-border-strong'];
const getIndentColor = (level) => indentColors[Math.min(level, indentColors.length - 1)];

// Indicateur de santé (inspiré Pipedrive) : progression vs temps écoulé
const computeHealth = (obj) => {
  if (!obj.date_debut || !obj.date_fin) return null;
  const now = new Date();
  const start = new Date(obj.date_debut);
  const end = new Date(obj.date_fin);
  if (now < start) return 'green';
  const totalDays = (end - start) / (1000 * 60 * 60 * 24);
  if (totalDays <= 0) return 'gray';
  const elapsed = Math.min(1, (now - start) / (end - start));
  const expectedProgress = elapsed * 100;
  const actualProgress = obj.progression || 0;
  if (actualProgress >= expectedProgress) return 'green';
  if (actualProgress >= expectedProgress * 0.7) return 'orange';
  return 'red';
};
const healthDot = (health) => {
  if (!health) return null;
  const colors = { green: 'bg-green-500', orange: 'bg-orange-400', red: 'bg-red-500', gray: 'bg-gray-300' };
  const labels = { green: 'En avance', orange: 'Attention', red: 'En retard', gray: 'Non démarré' };
  return <div className={`w-3 h-3 rounded-full ${colors[health]} flex-shrink-0`} title={labels[health]} />;
};

// ── Composant Étapes de suivi (4 jalons type Pipedrive) ──────────────────────
const EtapesSuivi = ({ obj, canEdit, onUpdateEtape }) => {
  const etapes = obj.etapes || [];
  const [editingEtape, setEditingEtape] = useState(null);
  const [etapeForm, setEtapeForm] = useState({});

  if (etapes.length === 0) return <p className="text-xs text-text-muted italic py-2">Aucune étape générée</p>;

  const startEditEtape = (etape) => {
    setEditingEtape(etape.id);
    setEtapeForm({
      valeur_actuelle: etape.valeur_actuelle || '',
      avances: etape.avances || '',
      retards: etape.retards || '',
      pourquoi: etape.pourquoi || '',
      ameliorations: etape.ameliorations || ''
    });
  };

  const saveEtape = async (etapeId) => {
    await onUpdateEtape(etapeId, etapeForm);
    setEditingEtape(null);
  };

  return (
    <div className="mt-3 space-y-2">
      {/* Stepper visuel (inspiré Pipedrive) */}
      <div className="flex items-center gap-1 mb-3">
        {etapes.map((etape, i) => {
          const actual = parseFloat(etape.valeur_actuelle) || 0;
          const expected = parseFloat(etape.valeur_attendue) || 0;
          const filled = etape.remplie_le != null;
          const color = filled
            ? (actual >= expected ? 'bg-green-500' : actual >= expected * 0.75 ? 'bg-orange-400' : 'bg-red-400')
            : 'bg-gray-200';
          const textColor = filled ? 'text-white' : 'text-text-muted';
          return (
            <React.Fragment key={etape.id}>
              {i > 0 && <div className={`flex-1 h-0.5 ${filled ? 'bg-green-300' : 'bg-gray-200'}`} />}
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${color} ${textColor}`}>
                  {i + 1}
                </div>
                <span className="text-[10px] text-text-muted mt-0.5">{fmtDate(etape.date_echeance)}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Détail des étapes */}
      {etapes.map((etape) => {
        const actual = parseFloat(etape.valeur_actuelle) || 0;
        const expected = parseFloat(etape.valeur_attendue) || 0;
        const isEditing = editingEtape === etape.id;

        return (
          <div key={etape.id} className="border border-border-subtle rounded-lg p-3 bg-elevated-1/50">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-text-secondary">
                Étape {etape.numero} — {fmtDate(etape.date_echeance)}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-text-muted">
                  {actual} / {expected} {obj.unite || ''}
                </span>
                {canEdit && !isEditing && (
                  <button onClick={() => startEditEtape(etape)} className="p-1 hover:bg-elevated-1 rounded">
                    <Edit2 className="w-3 h-3 text-text-muted" />
                  </button>
                )}
              </div>
            </div>

            {isEditing ? (
              <div className="space-y-2 mt-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-text-muted">Valeur actuelle</label>
                    <input type="number" value={etapeForm.valeur_actuelle}
                      onChange={e => setEtapeForm({ ...etapeForm, valeur_actuelle: e.target.value })}
                      className="w-full px-2 py-1 border rounded text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] text-text-muted">Avances</label>
                    <input type="text" value={etapeForm.avances}
                      onChange={e => setEtapeForm({ ...etapeForm, avances: e.target.value })}
                      className="w-full px-2 py-1 border rounded text-sm" placeholder="Points positifs" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-text-muted">Retards</label>
                    <input type="text" value={etapeForm.retards}
                      onChange={e => setEtapeForm({ ...etapeForm, retards: e.target.value })}
                      className="w-full px-2 py-1 border rounded text-sm" placeholder="Points de retard" />
                  </div>
                  <div>
                    <label className="text-[10px] text-text-muted">Pourquoi</label>
                    <input type="text" value={etapeForm.pourquoi}
                      onChange={e => setEtapeForm({ ...etapeForm, pourquoi: e.target.value })}
                      className="w-full px-2 py-1 border rounded text-sm" placeholder="Raisons" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-text-muted">Améliorations</label>
                  <input type="text" value={etapeForm.ameliorations}
                    onChange={e => setEtapeForm({ ...etapeForm, ameliorations: e.target.value })}
                    className="w-full px-2 py-1 border rounded text-sm" placeholder="Actions correctives" />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => saveEtape(etape.id)}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700">
                    Enregistrer
                  </button>
                  <button onClick={() => setEditingEtape(null)}
                    className="px-3 py-1 bg-gray-200 text-text-secondary rounded text-xs font-medium hover:bg-gray-300">
                    Annuler
                  </button>
                </div>
              </div>
            ) : (
              etape.remplie_le && (
                <div className="text-xs text-text-muted mt-1 space-y-0.5">
                  {etape.avances && <p><span className="text-green-600 dark:text-green-400 font-medium">Avances:</span> {etape.avances}</p>}
                  {etape.retards && <p><span className="text-red-600 dark:text-red-400 font-medium">Retards:</span> {etape.retards}</p>}
                  {etape.pourquoi && <p><span className="text-text-secondary font-medium">Pourquoi:</span> {etape.pourquoi}</p>}
                  {etape.ameliorations && <p><span className="text-blue-600 dark:text-blue-400 font-medium">Améliorations:</span> {etape.ameliorations}</p>}
                  {etape.remplie_par_nom && <p className="italic">Rempli par {etape.remplie_par_nom}</p>}
                </div>
              )
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Component ────────────────────────────────────────────────────────────────
const Objectifs = ({ token, user, employees = [], services: servicesProp = [], onNavigate }) => {
  const [objectifs, setObjectifs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [localServices, setLocalServices] = useState([]);
  const services = servicesProp.length > 0 ? servicesProp : localServices;
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedProjects, setExpandedProjects] = useState([]);
  const [expandedObjectifs, setExpandedObjectifs] = useState([]);
  const [expandedEtapes, setExpandedEtapes] = useState([]);

  // Filtre et recherche
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Modal découpage sous-objectifs
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitTarget, setSplitTarget] = useState(null);
  const [splitForm, setSplitForm] = useState({ count: 3, prefix: 'Phase' });

  // Action modal (quick add task)
  const [showAddAction, setShowAddAction] = useState(false);
  const [targetObjectif, setTargetObjectif] = useState(null);
  const [actionForm, setActionForm] = useState({ title: '', assigned_to: '', due_date: '', priority: 'Moyenne' });
  const [savingAction, setSavingAction] = useState(false);

  // Édition inline
  const [inlineEdit, setInlineEdit] = useState(null); // { id, field, value }

  const isAdmin = user && ['Patron', 'Super Manager'].includes(user.role);
  const isManager = user && user.role === 'Manager';
  const canEdit = isAdmin || isManager;

  const defaultForm = {
    titre: '', description: '', chiffrage: '', unite: '',
    project_id: '', parent_objectif_id: null,
    date_debut: '', date_fin: '',
    assignation_type: 'personne',
    proprietaire_id: '', assigned_service_id: '', assigned_all_project: 0
  };
  const [form, setForm] = useState(defaultForm);

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }), [token]);

  // ── Fermeture des modales par Escape ────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (showModal) closeModal();
      else if (showSplitModal) setShowSplitModal(false);
      else if (showAddAction) setShowAddAction(false);
      else if (inlineEdit) setInlineEdit(null);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  });

  // ── Data loading ───────────────────────────────────────────────────────────
  const reloadAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        fetch(`${API_URL}/objectifs`, { headers }),
        fetch(`${API_URL}/projects`, { headers }),
        servicesProp.length === 0 ? fetch(`${API_URL}/services`, { headers }) : Promise.resolve(null)
      ]);
      const [d1, d2] = await Promise.all([r1.json(), r2.json()]);
      if (r3) { const d3 = await r3.json(); setLocalServices(Array.isArray(d3) ? d3 : []); }
      setObjectifs(Array.isArray(d1) ? d1 : []);
      setProjects(Array.isArray(d2) ? d2 : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, headers, servicesProp.length]);

  useEffect(() => { reloadAll(); }, [reloadAll]);

  // ── CRUD ───────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.titre.trim()) return alert('Titre requis');

    setLoading(true);
    try {
      const url = editingId ? `${API_URL}/objectifs/${editingId}` : `${API_URL}/objectifs`;
      const method = editingId ? 'PUT' : 'POST';

      const body = {
        titre: form.titre,
        description: form.description,
        chiffrage: form.chiffrage,
        unite: form.unite,
        project_id: form.project_id || null,
        parent_objectif_id: form.parent_objectif_id || null,
        date_debut: form.date_debut,
        date_fin: form.date_fin,
        assigned_all_project: form.assignation_type === 'all' ? 1 : 0,
        assigned_service_id: form.assignation_type === 'service' ? (form.assigned_service_id || null) : null,
        proprietaire_id: form.assignation_type === 'personne' ? (form.proprietaire_id || null) : null
      };

      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Erreur');

      if (result.alerte_somme) {
        alert('La somme des objectifs/sous-objectifs ne correspond pas au chiffrage attendu.');
      }

      await reloadAll();
      closeModal();
    } catch (err) { alert(err.message); }
    setLoading(false);
  };

  const deleteObjectif = async (id) => {
    if (!window.confirm('Supprimer cet objectif et ses sous-objectifs ?')) return;
    try {
      await fetch(`${API_URL}/objectifs/${id}`, { method: 'DELETE', headers });
      await reloadAll();
    } catch { alert('Erreur lors de la suppression'); }
  };

  // ── Édition inline (titre, chiffrage, valeur_actuelle) ─────────────────────
  const saveInlineEdit = async () => {
    if (!inlineEdit) return;
    const { id, field, value } = inlineEdit;
    try {
      await fetch(`${API_URL}/objectifs/${id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ [field]: value })
      });
      await reloadAll();
    } catch { alert('Erreur de sauvegarde'); }
    setInlineEdit(null);
  };

  // ── Mise à jour d'une étape ────────────────────────────────────────────────
  const updateEtape = async (etapeId, data) => {
    try {
      await fetch(`${API_URL}/objectif-etapes/${etapeId}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ ...data, remplie_par: user?.employeeId || user?.id })
      });
      await reloadAll();
    } catch { alert('Erreur de mise à jour de l\'étape'); }
  };

  // ── Recalculer la progression depuis les tâches liées ─────────────────────
  const recalcProgression = async (objId) => {
    try {
      const res = await fetch(`${API_URL}/objectifs/${objId}/recalc`, { method: 'POST', headers });
      if (!res.ok) { const r = await res.json(); throw new Error(r.error || 'Erreur'); }
      await reloadAll();
    } catch (err) { alert(err.message); }
  };

  // ── Régénérer les 4 étapes de suivi ─────────────────────────────────────────
  const regenerateEtapes = async (objId) => {
    if (!window.confirm('Régénérer les 4 étapes de suivi ? Les données existantes seront écrasées.')) return;
    try {
      const res = await fetch(`${API_URL}/objectifs/${objId}/etapes/generate`, { method: 'POST', headers });
      if (!res.ok) { const r = await res.json(); throw new Error(r.error || 'Erreur'); }
      await reloadAll();
    } catch (err) { alert(err.message); }
  };

  // ── Découpage en sous-objectifs ────────────────────────────────────────────
  const handleSplit = async () => {
    if (!splitTarget) return;
    const { count, prefix } = splitForm;
    const parentChiffrage = parseFloat(splitTarget.chiffrage) || 0;
    const perChild = parentChiffrage > 0 ? Math.round((parentChiffrage / count) * 100) / 100 : 0;

    // Calculer les tranches de dates
    const start = new Date(splitTarget.date_debut);
    const end = new Date(splitTarget.date_fin);
    const totalMs = end - start;
    const sliceMs = totalMs / count;

    setLoading(true);
    try {
      for (let i = 0; i < count; i++) {
        const sliceStart = new Date(start.getTime() + sliceMs * i);
        const sliceEnd = new Date(start.getTime() + sliceMs * (i + 1));
        // Dernier sous-objectif reçoit le restant du chiffrage
        const chiffrage = i === count - 1 ? Math.round((parentChiffrage - perChild * (count - 1)) * 100) / 100 : perChild;

        await fetch(`${API_URL}/objectifs`, {
          method: 'POST', headers,
          body: JSON.stringify({
            titre: `${prefix} ${i + 1}`,
            project_id: splitTarget.project_id,
            parent_objectif_id: splitTarget.id,
            chiffrage,
            unite: splitTarget.unite,
            date_debut: sliceStart.toISOString().split('T')[0],
            date_fin: sliceEnd.toISOString().split('T')[0],
          })
        });
      }
      await reloadAll();
      setShowSplitModal(false);
      setSplitTarget(null);
      // Développer l'objectif parent pour voir les sous-objectifs créés
      if (!expandedObjectifs.includes(splitTarget.id)) {
        setExpandedObjectifs(prev => [...prev, splitTarget.id]);
      }
    } catch (err) { alert(err.message); }
    setLoading(false);
  };

  // ── Quick-add task linked to objectif ──────────────────────────────────────
  const submitAction = async () => {
    if (!actionForm.title.trim() || !targetObjectif) return;
    setSavingAction(true);
    try {
      await fetch(`${API_URL}/tasks`, {
        method: 'POST', headers,
        body: JSON.stringify({
          title: actionForm.title,
          assigned_to: actionForm.assigned_to || null,
          due_date: actionForm.due_date || null,
          priority: actionForm.priority,
          project_id: targetObjectif.project_id,
          objectif_id: targetObjectif.id,
          status: 'A faire'
        })
      });
      setShowAddAction(false);
      setActionForm({ title: '', assigned_to: '', due_date: '', priority: 'Moyenne' });
      setTargetObjectif(null);
      await reloadAll();
    } catch { alert('Erreur'); }
    setSavingAction(false);
  };

  // ── Modal management ───────────────────────────────────────────────────────
  const openCreate = (projectId = '', parentId = null) => {
    const proj = projects.find(p => p.id === parseInt(projectId));
    // Si on crée un sous-objectif, utiliser les dates du parent
    let parentObj = null;
    if (parentId) {
      const findObj = (list) => {
        for (const o of list) {
          if (o.id === parentId) return o;
          if (o.sous_objectifs?.length) {
            const found = findObj(o.sous_objectifs);
            if (found) return found;
          }
        }
        return null;
      };
      parentObj = findObj(objectifs);
    }

    setEditingId(null);
    setForm({
      ...defaultForm,
      project_id: projectId,
      parent_objectif_id: parentId,
      date_debut: parentObj?.date_debut || proj?.start_date || '',
      date_fin: parentObj?.date_fin || proj?.end_date || '',
      unite: parentObj?.unite || proj?.unite || ''
    });
    setShowModal(true);
  };

  const openEdit = (obj) => {
    setEditingId(obj.id);
    const aType = obj.assigned_all_project ? 'all' : obj.assigned_service_id ? 'service' : 'personne';
    setForm({
      titre: obj.titre || '',
      description: obj.description || '',
      chiffrage: obj.chiffrage || '',
      unite: obj.unite || '',
      project_id: obj.project_id || '',
      parent_objectif_id: obj.parent_objectif_id || null,
      date_debut: obj.date_debut || '',
      date_fin: obj.date_fin || '',
      assignation_type: aType,
      proprietaire_id: obj.proprietaire_id || '',
      assigned_service_id: obj.assigned_service_id || '',
      assigned_all_project: obj.assigned_all_project || 0
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(defaultForm);
  };

  // ── Filtre récursif sur l'arbre d'objectifs ───────────────────────────────
  const filterTree = useCallback((list) => {
    if (!searchQuery && statusFilter === 'all') return list;
    const q = searchQuery.toLowerCase();
    const matches = (obj) => {
      const titleMatch = !q || obj.titre?.toLowerCase().includes(q) || obj.proprietaire_nom?.toLowerCase().includes(q);
      const statusMatch = statusFilter === 'all' || obj.statut === statusFilter;
      return titleMatch && statusMatch;
    };
    const filterNode = (obj) => {
      const filteredChildren = (obj.sous_objectifs || []).map(filterNode).filter(Boolean);
      if (matches(obj) || filteredChildren.length > 0) {
        return { ...obj, sous_objectifs: filteredChildren };
      }
      return null;
    };
    return list.map(filterNode).filter(Boolean);
  }, [searchQuery, statusFilter]);

  // ── Group objectifs by project ─────────────────────────────────────────────
  const groupedByProject = useMemo(() => {
    const filtered = filterTree(objectifs);
    const groups = {};
    filtered.forEach(obj => {
      const pid = obj.project_id || 'sans_projet';
      if (!groups[pid]) groups[pid] = [];
      groups[pid].push(obj);
    });
    return groups;
  }, [objectifs, filterTree]);

  // Helper: get project members for the currently selected project
  const getProjectMembers = (projectId) => {
    if (!projectId) return employees.filter(e => !e.deleted_at);
    const proj = projects.find(p => p.id === parseInt(projectId));
    if (!proj?.members?.length) return employees.filter(e => !e.deleted_at);
    return proj.members.map(m => {
      const emp = employees.find(e => e.id === m.employee_id);
      return emp || m;
    }).filter(Boolean);
  };

  // Current project for date validation
  const currentProject = form.project_id ? projects.find(p => p.id === parseInt(form.project_id)) : null;

  // Compute running sum for alert display
  const computeSumAlert = () => {
    if (!form.project_id) return null;
    const proj = projects.find(p => p.id === parseInt(form.project_id));
    if (!proj) return null;

    if (form.parent_objectif_id) {
      const findParent = (list) => {
        for (const o of list) {
          if (o.id === form.parent_objectif_id) return o;
          if (o.sous_objectifs?.length) {
            const found = findParent(o.sous_objectifs);
            if (found) return found;
          }
        }
        return null;
      };
      const parent = findParent(objectifs);
      if (!parent) return null;
      const siblings = (parent.sous_objectifs || []).filter(o => o.id !== editingId);
      const sum = siblings.reduce((s, o) => s + (parseFloat(o.chiffrage) || 0), 0) + (parseFloat(form.chiffrage) || 0);
      const target = parseFloat(parent.chiffrage) || 0;
      return { sum, target, unite: parent.unite || proj.unite, label: "objectif parent" };
    } else {
      const rootSiblings = objectifs.filter(o => o.project_id === parseInt(form.project_id) && o.id !== editingId);
      const sum = rootSiblings.reduce((s, o) => s + (parseFloat(o.chiffrage) || 0), 0) + (parseFloat(form.chiffrage) || 0);
      const target = parseFloat(proj.chiffre) || 0;
      return { sum, target, unite: proj.unite, label: "projet" };
    }
  };

  const sumAlert = computeSumAlert();

  // ── Compteurs récursifs ────────────────────────────────────────────────────
  const countAllObjectifs = (list) => {
    let count = 0;
    list.forEach(o => {
      count++;
      if (o.sous_objectifs?.length) count += countAllObjectifs(o.sous_objectifs);
    });
    return count;
  };

  const countByStatus = (list, status) => {
    let count = 0;
    list.forEach(o => {
      if (o.statut === status) count++;
      if (o.sous_objectifs?.length) count += countByStatus(o.sous_objectifs, status);
    });
    return count;
  };

  const countAlertes = (list) => {
    let count = 0;
    list.forEach(o => {
      if (o.alerte_somme) count++;
      if (o.sous_objectifs?.length) count += countAlertes(o.sous_objectifs);
    });
    return count;
  };


  // ── Render objectif card (récursif, profondeur N) ──────────────────────────
  const ObjectifCard = ({ obj, level = 0 }) => {
    const sty = getStatusStyle(obj.statut);
    const isExpanded = expandedObjectifs.includes(obj.id);
    const isEtapesExpanded = expandedEtapes.includes(obj.id);
    const sousObjs = obj.sous_objectifs || [];
    const health = computeHealth(obj);
    const progressionVal = obj.chiffrage && parseFloat(obj.chiffrage) > 0
      ? pct(obj.valeur_actuelle || 0, parseFloat(obj.chiffrage))
      : (obj.progression || 0);

    // Barre segmentée type Monday.com (si sous-objectifs)
    const renderSegmentedBar = () => {
      if (sousObjs.length === 0) return null;
      const done = sousObjs.filter(s => s.statut === 'termine' || s.statut === 'atteint').length;
      const inProgress = sousObjs.filter(s => s.statut === 'en_cours').length;
      const total = sousObjs.length;
      const pDone = (done / total) * 100;
      const pInProgress = (inProgress / total) * 100;
      return (
        <div className="flex h-1.5 rounded-full overflow-hidden bg-elevated-1 mt-1">
          {pDone > 0 && <div className="bg-green-500" style={{ width: `${pDone}%` }} />}
          {pInProgress > 0 && <div className="bg-blue-400" style={{ width: `${pInProgress}%` }} />}
        </div>
      );
    };

    return (
      <div className={`${level > 0 ? `ml-6 border-l-2 ${getIndentColor(level)} pl-4` : ''}`}>
        <div className={`bg-surface rounded-lg border ${obj.alerte_somme ? 'border-orange-300 dark:border-orange-700 bg-orange-50/30' : 'border-border-subtle'} p-4 mb-2 hover:shadow-md transition group`}>
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              {/* Titre + Status + Santé */}
              <div className="flex items-center gap-2 flex-wrap mb-1">
                {sousObjs.length > 0 && (
                  <button onClick={() => setExpandedObjectifs(prev =>
                    prev.includes(obj.id) ? prev.filter(id => id !== obj.id) : [...prev, obj.id]
                  )} className="p-0.5 flex-shrink-0">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-text-muted" /> : <ChevronRight className="w-4 h-4 text-text-muted" />}
                  </button>
                )}
                {healthDot(health)}

                {/* Titre — double-clic pour édition inline */}
                {inlineEdit?.id === obj.id && inlineEdit?.field === 'titre' ? (
                  <input autoFocus type="text" value={inlineEdit.value}
                    onChange={e => setInlineEdit({ ...inlineEdit, value: e.target.value })}
                    onBlur={saveInlineEdit}
                    onKeyDown={e => e.key === 'Enter' && saveInlineEdit()}
                    className="font-semibold text-text-primary border-b-2 border-blue-400 dark:border-blue-600 bg-blue-50/30 outline-none px-1" />
                ) : (
                  <h4 className={`font-semibold text-text-primary truncate ${level > 0 ? 'text-sm' : ''} ${canEdit ? 'cursor-text' : ''}`}
                    onDoubleClick={() => canEdit && setInlineEdit({ id: obj.id, field: 'titre', value: obj.titre })}>
                    {obj.titre}
                  </h4>
                )}

                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sty.bg} ${sty.text}`}>{sty.label}</span>
                {sousObjs.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded text-xs bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 font-medium flex-shrink-0">
                    {sousObjs.length} ss-obj
                  </span>
                )}
                {obj.alerte_somme && (
                  <span className="text-orange-500" title="Somme sous-objectifs != chiffrage">
                    <AlertTriangle className="w-4 h-4" />
                  </span>
                )}
              </div>

              {/* Details */}
              <div className="flex items-center gap-3 text-xs text-text-muted mb-2 flex-wrap">
                {obj.date_debut && obj.date_fin && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {fmtDate(obj.date_debut)} - {fmtDate(obj.date_fin)}
                  </span>
                )}
                {obj.chiffrage && (
                  inlineEdit?.id === obj.id && inlineEdit?.field === 'chiffrage' ? (
                    <input autoFocus type="number" value={inlineEdit.value}
                      onChange={e => setInlineEdit({ ...inlineEdit, value: e.target.value })}
                      onBlur={saveInlineEdit}
                      onKeyDown={e => e.key === 'Enter' && saveInlineEdit()}
                      className="w-24 font-semibold text-text-secondary border-b-2 border-blue-400 dark:border-blue-600 bg-blue-50/30 outline-none px-1 text-xs" />
                  ) : (
                    <span className={`font-semibold text-text-secondary ${canEdit ? 'cursor-text' : ''}`}
                      onDoubleClick={() => canEdit && setInlineEdit({ id: obj.id, field: 'chiffrage', value: obj.chiffrage })}>
                      {fmtNum(obj.chiffrage, obj.unite)}
                    </span>
                  )
                )}
                {obj.proprietaire_nom && (
                  <span className="flex items-center gap-1 text-indigo-600 dark:text-indigo-400">
                    <Users className="w-3 h-3" /> {obj.proprietaire_nom}
                  </span>
                )}
                {obj.assigned_all_project ? (
                  <span className="bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 px-1.5 py-0.5 rounded text-xs">Tout le projet</span>
                ) : obj.assigned_service_name ? (
                  <span className="flex items-center gap-1 bg-teal-50 dark:bg-teal-950/40 text-teal-600 dark:text-teal-400 px-1.5 py-0.5 rounded text-xs">
                    <Building2 className="w-3 h-3" /> {obj.assigned_service_name}
                  </span>
                ) : null}
                {obj.task_count > 0 && (
                  <span className="flex items-center gap-1">
                    <CheckSquare className="w-3 h-3" /> {obj.task_done_count || 0}/{obj.task_count} tâches
                  </span>
                )}
              </div>

              {/* Valeur actuelle — édition inline */}
              {obj.chiffrage && parseFloat(obj.chiffrage) > 0 && (
                <div className="flex items-center gap-2 text-xs text-text-muted mb-1">
                  <span>Réalisé:</span>
                  {inlineEdit?.id === obj.id && inlineEdit?.field === 'valeur_actuelle' ? (
                    <input autoFocus type="number" value={inlineEdit.value}
                      onChange={e => setInlineEdit({ ...inlineEdit, value: e.target.value })}
                      onBlur={saveInlineEdit}
                      onKeyDown={e => { if (e.key === 'Enter') saveInlineEdit(); if (e.key === 'Escape') setInlineEdit(null); }}
                      className="w-24 border-b-2 border-blue-400 bg-blue-50/30 outline-none px-1 text-sm font-semibold text-text-secondary" />
                  ) : (
                    <span className={`font-semibold text-text-secondary ${canEdit ? 'cursor-text hover:bg-blue-50 dark:bg-blue-950/40 px-1 rounded' : ''}`}
                      onDoubleClick={() => canEdit && setInlineEdit({ id: obj.id, field: 'valeur_actuelle', value: obj.valeur_actuelle || 0 })}>
                      {fmtNum(obj.valeur_actuelle || 0, obj.unite)}
                    </span>
                  )}
                  <span>/ {fmtNum(obj.chiffrage, obj.unite)}</span>
                </div>
              )}

              {/* Barre de progression */}
              {sousObjs.length > 0 ? renderSegmentedBar() : (
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-elevated-1 rounded-full h-1.5">
                    <div className={`h-1.5 rounded-full transition-all ${progressColor(progressionVal)}`}
                      style={{ width: `${Math.min(100, progressionVal)}%` }} />
                  </div>
                  <span className="text-xs font-medium text-text-secondary w-10 text-right">{progressionVal}%</span>
                </div>
              )}

              {/* Alerte somme */}
              {obj.alerte_somme && obj.somme_sous_objectifs !== undefined && (
                <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                  Somme sous-obj: {fmtNum(obj.somme_sous_objectifs, obj.unite)} / {fmtNum(obj.chiffrage, obj.unite)}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 ml-2 opacity-0 group-hover:opacity-100 transition flex-shrink-0">
              {/* Navigation vers le projet */}
              {onNavigate && obj.project_id && (
                <button onClick={() => onNavigate('projets')}
                  className="p-1.5 text-text-muted hover:bg-elevated-1 rounded" title="Voir le projet">
                  <ExternalLink className="w-4 h-4" />
                </button>
              )}
              {canEdit && (
                <>
                  <button onClick={() => openCreate(obj.project_id, obj.id)}
                    className="p-1.5 text-green-600 hover:bg-green-50 dark:bg-green-950/40 rounded" title="Ajouter sous-objectif">
                    <Plus className="w-4 h-4" />
                  </button>
                  {sousObjs.length === 0 && obj.chiffrage && obj.date_debut && obj.date_fin && (
                    <button onClick={() => { setSplitTarget(obj); setShowSplitModal(true); }}
                      className="p-1.5 text-purple-600 hover:bg-purple-50 dark:bg-purple-950/40 rounded" title="Découper en sous-objectifs">
                      <Scissors className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => {
                    setExpandedEtapes(prev => prev.includes(obj.id) ? prev.filter(id => id !== obj.id) : [...prev, obj.id]);
                  }}
                    className="p-1.5 text-amber-600 hover:bg-amber-50 dark:bg-amber-950/40 rounded" title="Étapes de suivi">
                    <ClipboardList className="w-4 h-4" />
                  </button>
                  <button onClick={() => recalcProgression(obj.id)}
                    className="p-1.5 text-cyan-600 hover:bg-cyan-50 dark:bg-cyan-950/40 rounded" title="Recalculer progression">
                    <RotateCcw className="w-4 h-4" />
                  </button>
                  <button onClick={() => { setTargetObjectif(obj); setShowAddAction(true); }}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 dark:bg-blue-950/40 rounded" title="Ajouter tâche liée">
                    <CheckSquare className="w-4 h-4" />
                  </button>
                  <button onClick={() => openEdit(obj)}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 dark:bg-blue-950/40 rounded" title="Modifier">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteObjectif(obj.id)}
                    className="p-1.5 text-red-600 hover:bg-red-50 dark:bg-red-950/40 rounded" title="Supprimer">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Étapes de suivi dépliables */}
        {isEtapesExpanded && (
          <div className="mb-2 ml-4 bg-amber-50/50 rounded-lg border border-amber-200 p-3">
            <div className="flex items-center justify-between mb-1">
              <h5 className="text-xs font-semibold text-amber-700 dark:text-amber-300 flex items-center gap-1">
                <BarChart3 className="w-3.5 h-3.5" /> Étapes de suivi
              </h5>
              {canEdit && (
                <button onClick={() => regenerateEtapes(obj.id)}
                  className="text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 rounded flex items-center gap-1">
                  <RotateCcw className="w-3 h-3" /> Régénérer
                </button>
              )}
            </div>
            <EtapesSuivi obj={obj} canEdit={canEdit} onUpdateEtape={updateEtape} />
          </div>
        )}

        {/* Sous-objectifs récursifs (indentés) */}
        {isExpanded && sousObjs.length > 0 && (
          <div className="mb-2">
            {sousObjs.map(so => (
              <ObjectifCard key={so.id} obj={so} level={level + 1} />
            ))}
            {/* Ligne résumé (inspiré Smartsheet) */}
            {sousObjs.length > 1 && (
              <div className={`ml-6 border-l-2 ${getIndentColor(level + 1)} pl-4`}>
                <div className="bg-elevated-1 rounded border border-border-subtle px-4 py-1.5 mb-2 text-xs text-text-muted italic flex items-center gap-4">
                  <span>{sousObjs.length} sous-objectifs</span>
                  <span>Somme: {fmtNum(sousObjs.reduce((s, o) => s + (parseFloat(o.chiffrage) || 0), 0), obj.unite)}</span>
                  <span>Progression moy: {Math.round(sousObjs.reduce((s, o) => s + (o.progression || 0), 0) / sousObjs.length)}%</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Main Render ────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-surface rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Target className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            <div>
              <h1 className="text-2xl font-bold text-text-primary">Objectifs</h1>
              <p className="text-text-muted text-sm">Arbre hiérarchique par projet avec sous-objectifs datés</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={reloadAll} className="p-2 hover:bg-elevated-1 rounded-lg" title="Rafraîchir">
              <RefreshCw className={`w-5 h-5 text-text-muted ${loading ? 'animate-spin' : ''}`} />
            </button>
            {canEdit && (
              <button onClick={() => openCreate()}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-lg flex items-center gap-2 font-medium shadow transition">
                <Plus className="w-5 h-5" /> Nouvel Objectif
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total objectifs', value: countAllObjectifs(objectifs), color: 'text-text-primary', icon: Target, iconBg: 'bg-elevated-1' },
          { label: 'En cours', value: countByStatus(objectifs, 'en_cours'), color: 'text-blue-600', icon: TrendingUp, iconBg: 'bg-blue-50 dark:bg-blue-950/40' },
          { label: 'Terminés', value: countByStatus(objectifs, 'termine') + countByStatus(objectifs, 'atteint'), color: 'text-green-600', icon: CheckSquare, iconBg: 'bg-green-50 dark:bg-green-950/40' },
          { label: 'Alertes', value: countAlertes(objectifs), color: 'text-orange-600', icon: AlertTriangle, iconBg: 'bg-orange-50 dark:bg-orange-950/40' },
        ].map((s, i) => (
          <div key={i} className="bg-surface rounded-xl shadow-sm border p-4">
            <div className="flex items-center gap-2 mb-1">
              <div className={`p-1.5 rounded-lg ${s.iconBg}`}>
                <s.icon className={`w-4 h-4 ${s.color}`} />
              </div>
              <span className="text-text-muted text-sm">{s.label}</span>
            </div>
            <p className={`text-3xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Barre de recherche et filtres */}
      <div className="bg-surface rounded-xl shadow-sm border p-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input type="text" value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Rechercher un objectif, un responsable..."
              className="w-full pl-10 pr-4 py-2 border border-border-strong rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg border transition ${showFilters ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 text-emerald-600 dark:text-emerald-400' : 'border-border-strong text-text-muted hover:bg-elevated-1'}`}
              title="Filtrer par statut">
              <Filter className="w-4 h-4" />
            </button>
            {(searchQuery || statusFilter !== 'all') && (
              <button onClick={() => { setSearchQuery(''); setStatusFilter('all'); }}
                className="text-xs text-text-muted hover:text-text-secondary px-2 py-1 rounded hover:bg-elevated-1">
                Réinitialiser
              </button>
            )}
          </div>
        </div>
        {showFilters && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
            {[{ value: 'all', label: 'Tous' }, ...statusOpts].map(opt => (
              <button key={opt.value}
                onClick={() => setStatusFilter(opt.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                  statusFilter === opt.value
                    ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-300'
                    : 'bg-elevated-1 text-text-secondary hover:bg-elevated-1'
                }`}>
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Objectifs grouped by project (accordion) */}
      <div className="space-y-3">
        {projects.length > 0 ? projects.map(proj => {
          const projObjs = groupedByProject[proj.id] || [];
          const isExpanded = expandedProjects.includes(proj.id);
          const sommeObjs = projObjs.reduce((s, o) => s + (parseFloat(o.chiffrage) || 0), 0);
          const projChiffre = parseFloat(proj.chiffre) || 0;
          const hasAlert = projChiffre > 0 && Math.abs(sommeObjs - projChiffre) > 0.01;

          return (
            <div key={proj.id} className="bg-surface rounded-xl shadow overflow-hidden">
              <button
                onClick={() => setExpandedProjects(prev =>
                  prev.includes(proj.id) ? prev.filter(id => id !== proj.id) : [...prev, proj.id]
                )}
                className={`w-full flex items-center justify-between p-4 hover:bg-elevated-1 transition text-left ${hasAlert ? 'border-l-4 border-orange-400 dark:border-orange-600' : ''}`}
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? <ChevronDown className="w-5 h-5 text-text-muted" /> : <ChevronRight className="w-5 h-5 text-text-muted" />}
                  <Folder className="w-5 h-5 text-blue-500" />
                  <span className="font-bold text-text-primary">{proj.name}</span>
                  <span className="text-xs bg-elevated-1 text-text-secondary px-2 py-0.5 rounded-full">
                    {projObjs.length} objectif{projObjs.length > 1 ? 's' : ''}
                  </span>
                  {proj.chiffre && (
                    <span className="text-xs text-text-muted">
                      Cible: {fmtNum(proj.chiffre, proj.unite)}
                    </span>
                  )}
                  {hasAlert && (
                    <span className="flex items-center gap-1 text-orange-500 text-xs">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Somme: {fmtNum(sommeObjs, proj.unite)}
                    </span>
                  )}
                </div>
                {canEdit && (
                  <button onClick={(e) => { e.stopPropagation(); openCreate(proj.id); }}
                    className="text-emerald-600 hover:bg-emerald-50 dark:bg-emerald-950/40 p-1.5 rounded"
                    title="Ajouter objectif à ce projet">
                    <Plus className="w-4 h-4" />
                  </button>
                )}
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 pt-1 bg-elevated-1/50">
                  {projObjs.length > 0 ? (
                    projObjs.map(obj => <ObjectifCard key={obj.id} obj={obj} />)
                  ) : (
                    <p className="text-text-muted text-sm text-center py-4">Aucun objectif pour ce projet</p>
                  )}
                </div>
              )}
            </div>
          );
        }) : (
          <div className="bg-surface rounded-xl shadow p-8 text-center">
            <p className="text-text-muted">Aucun projet. Créez d'abord un projet.</p>
          </div>
        )}

        {/* Objectifs sans projet */}
        {groupedByProject['sans_projet']?.length > 0 && (
          <div className="bg-surface rounded-xl shadow overflow-hidden">
            <button
              onClick={() => setExpandedProjects(prev =>
                prev.includes('sans_projet') ? prev.filter(id => id !== 'sans_projet') : [...prev, 'sans_projet']
              )}
              className="w-full flex items-center justify-between p-4 hover:bg-elevated-1 transition text-left"
            >
              <div className="flex items-center gap-3">
                {expandedProjects.includes('sans_projet') ? <ChevronDown className="w-5 h-5 text-text-muted" /> : <ChevronRight className="w-5 h-5 text-text-muted" />}
                <Target className="w-5 h-5 text-text-muted" />
                <span className="font-bold text-text-secondary">Objectifs sans projet</span>
                <span className="text-xs bg-elevated-1 text-text-secondary px-2 py-0.5 rounded-full">
                  {groupedByProject['sans_projet'].length}
                </span>
              </div>
            </button>
            {expandedProjects.includes('sans_projet') && (
              <div className="px-4 pb-4 pt-1 bg-elevated-1/50">
                {groupedByProject['sans_projet'].map(obj => <ObjectifCard key={obj.id} obj={obj} />)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Modal Création / Modification ──────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="w-full sm:max-w-2xl sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface shadow-2xl">
            <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-surface shrink-0">
              <h2 className="text-lg sm:text-xl font-bold text-text-primary">
                {editingId ? 'Modifier l\'objectif' : form.parent_objectif_id ? 'Nouveau sous-objectif' : 'Nouvel objectif'}
              </h2>
              <button onClick={closeModal} className="p-2 hover:bg-elevated-1 rounded-lg">
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Titre *</label>
                <input type="text" required value={form.titre}
                  onChange={e => setForm({ ...form, titre: e.target.value })}
                  className="w-full px-4 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border border-border-strong rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Titre de l'objectif" />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Description</label>
                <textarea value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  className="w-full px-4 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border border-border-strong rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  rows="2" placeholder="Description..." />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Projet</label>
                <select value={form.project_id}
                  onChange={e => {
                    const pid = e.target.value;
                    const proj = projects.find(p => p.id === parseInt(pid));
                    setForm({
                      ...form,
                      project_id: pid,
                      date_debut: proj?.start_date || form.date_debut,
                      date_fin: proj?.end_date || form.date_fin,
                      unite: proj?.unite || form.unite,
                      parent_objectif_id: null
                    });
                  }}
                  className="w-full px-4 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border border-border-strong rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent">
                  <option value="">Aucun projet</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Date début *</label>
                  <input type="date" value={form.date_debut}
                    onChange={e => setForm({ ...form, date_debut: e.target.value })}
                    className="w-full px-4 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border border-border-strong rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Date fin *</label>
                  <input type="date" value={form.date_fin}
                    onChange={e => setForm({ ...form, date_fin: e.target.value })}
                    className="w-full px-4 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border border-border-strong rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
                </div>
              </div>
              {form.date_debut && form.date_fin && currentProject && (
                <p className="text-xs text-text-muted -mt-3">
                  Période projet: {fmtDate(currentProject.start_date)} - {fmtDate(currentProject.end_date)}
                </p>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Chiffrage</label>
                  <input type="text" value={form.chiffrage}
                    onChange={e => setForm({ ...form, chiffrage: e.target.value })}
                    className="w-full px-4 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border border-border-strong rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Ex: 50000" />
                  {sumAlert && sumAlert.target > 0 && (
                    <p className={`text-xs mt-1 ${Math.abs(sumAlert.sum - sumAlert.target) > 0.01 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                      Somme: {fmtNum(sumAlert.sum, sumAlert.unite)} / {fmtNum(sumAlert.target, sumAlert.unite)} ({sumAlert.label})
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Unité</label>
                  <input type="text" value={form.unite}
                    onChange={e => setForm({ ...form, unite: e.target.value })}
                    className="w-full px-4 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border border-border-strong rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Ex: €, contrats" />
                </div>
              </div>

              {form.project_id && (
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Assignation</label>
                  <div className="flex flex-wrap gap-3 mb-3">
                    {[
                      { value: 'all', label: 'Tout le projet' },
                      { value: 'service', label: 'Tout le service' },
                      { value: 'personne', label: 'Une personne' }
                    ].map(opt => (
                      <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer text-sm min-h-[44px]">
                        <input type="radio" name="assignation" value={opt.value}
                          checked={form.assignation_type === opt.value}
                          onChange={() => setForm({ ...form, assignation_type: opt.value })}
                          className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                        {opt.label}
                      </label>
                    ))}
                  </div>

                  {form.assignation_type === 'personne' && (
                    <select value={form.proprietaire_id}
                      onChange={e => setForm({ ...form, proprietaire_id: parseInt(e.target.value) || '' })}
                      className="w-full px-4 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border border-border-strong rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent">
                      <option value="">Sélectionner un membre</option>
                      {getProjectMembers(form.project_id).map(emp => (
                        <option key={emp.id || emp.employee_id} value={emp.id || emp.employee_id}>
                          {emp.first_name} {emp.last_name}
                        </option>
                      ))}
                    </select>
                  )}

                  {form.assignation_type === 'service' && (
                    <select value={form.assigned_service_id}
                      onChange={e => setForm({ ...form, assigned_service_id: parseInt(e.target.value) || '' })}
                      className="w-full px-4 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border border-border-strong rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent">
                      <option value="">Sélectionner un service</option>
                      {services.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}
              </div>

              <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
                <button type="button" onClick={closeModal}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-elevated-1 hover:bg-elevated-1 text-text-secondary transition">
                  Annuler
                </button>
                <button type="submit" disabled={loading}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white transition">
                  {loading ? 'En cours...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal Découpage (Split) ───────────────────────────────────────────── */}
      {showSplitModal && splitTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={e => e.target === e.currentTarget && setShowSplitModal(false)}>
          <div className="w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface shadow-2xl">
            <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-surface shrink-0">
              <h3 className="font-bold text-text-primary flex items-center gap-2">
                <Scissors className="w-5 h-5 text-purple-600 dark:text-purple-400" /> Découper en sous-objectifs
              </h3>
              <button onClick={() => setShowSplitModal(false)} className="p-2 hover:bg-elevated-1 rounded-lg">
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
              <div className="bg-purple-50 dark:bg-purple-950/40 rounded-lg p-3">
                <p className="text-sm font-medium text-purple-800">{splitTarget.titre}</p>
                <p className="text-xs text-purple-600 dark:text-purple-400">
                  {fmtNum(splitTarget.chiffrage, splitTarget.unite)} | {fmtDate(splitTarget.date_debut)} - {fmtDate(splitTarget.date_fin)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Nombre de sous-objectifs</label>
                <input type="number" min="2" max="12" value={splitForm.count}
                  onChange={e => setSplitForm({ ...splitForm, count: parseInt(e.target.value) || 2 })}
                  className="w-full px-4 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border border-border-strong rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Préfixe des titres</label>
                <input type="text" value={splitForm.prefix}
                  onChange={e => setSplitForm({ ...splitForm, prefix: e.target.value })}
                  className="w-full px-4 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border border-border-strong rounded-lg"
                  placeholder="Ex: Phase, Trimestre, Mois" />
              </div>

              <div className="bg-elevated-1 rounded-lg p-3 text-xs text-text-secondary">
                <p className="font-medium mb-1">Aperçu :</p>
                {Array.from({ length: splitForm.count }).map((_, i) => {
                  const parentChiffrage = parseFloat(splitTarget.chiffrage) || 0;
                  const perChild = Math.round((parentChiffrage / splitForm.count) * 100) / 100;
                  return (
                    <p key={i}>
                      {splitForm.prefix} {i + 1} — {fmtNum(perChild, splitTarget.unite)}
                    </p>
                  );
                })}
              </div>
            </div>

            <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
              <button onClick={() => setShowSplitModal(false)}
                className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-elevated-1 hover:bg-elevated-1 text-text-secondary">
                Annuler
              </button>
              <button onClick={handleSplit} disabled={loading}
                className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white">
                {loading ? 'Découpage...' : 'Découper'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Add Action (Task) ──────────────────────────────────────────── */}
      {showAddAction && targetObjectif && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={e => e.target === e.currentTarget && setShowAddAction(false)}>
          <div className="w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface shadow-2xl">
            <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-surface shrink-0">
              <div>
                <h3 className="font-bold text-text-primary">Nouvelle tâche liée</h3>
                <p className="text-xs text-text-muted">Objectif: {targetObjectif.titre}</p>
              </div>
              <button onClick={() => setShowAddAction(false)} className="p-2 hover:bg-elevated-1 rounded-lg">
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
              <input type="text" value={actionForm.title}
                onChange={e => setActionForm({ ...actionForm, title: e.target.value })}
                className="w-full px-4 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border border-border-strong rounded-lg"
                placeholder="Titre de la tâche *" />

              <select value={actionForm.assigned_to}
                onChange={e => setActionForm({ ...actionForm, assigned_to: e.target.value })}
                className="w-full px-4 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border border-border-strong rounded-lg">
                <option value="">Assigner à...</option>
                {getProjectMembers(targetObjectif.project_id).map(emp => (
                  <option key={emp.id || emp.employee_id} value={emp.id || emp.employee_id}>
                    {emp.first_name} {emp.last_name}
                  </option>
                ))}
              </select>

              <div className="grid grid-cols-2 gap-3">
                <input type="date" value={actionForm.due_date}
                  onChange={e => setActionForm({ ...actionForm, due_date: e.target.value })}
                  className="px-4 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border border-border-strong rounded-lg" />
                <select value={actionForm.priority}
                  onChange={e => setActionForm({ ...actionForm, priority: e.target.value })}
                  className="px-4 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border border-border-strong rounded-lg">
                  <option value="Faible">Faible</option>
                  <option value="Moyenne">Moyenne</option>
                  <option value="Haute">Haute</option>
                </select>
              </div>
            </div>

            <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
              <button onClick={() => setShowAddAction(false)}
                className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-elevated-1 hover:bg-elevated-1 text-text-secondary">
                Annuler
              </button>
              <button onClick={submitAction} disabled={savingAction || !actionForm.title.trim()}
                className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white">
                {savingAction ? 'Ajout...' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Objectifs;
